import { type ComponentType, lazy } from 'react';

type Importer<T> = () => Promise<{ default: ComponentType<T> }>;

const RELOAD_FLAG_PREFIX = 'chunk-reload:';

/**
 * Detects the "stale chunk after deploy" failure. After a deployment the old
 * hashed chunk filenames no longer exist on the server, so a dynamic `import()`
 * from an already-loaded tab rejects with one of these messages.
 */
function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    (error instanceof Error && error.name === 'ChunkLoadError')
  );
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Drop-in replacement for React.lazy that survives deploys.
 *
 * 1. Retries the import a few times to ride out transient network blips.
 * 2. If it still fails with a chunk error, the user's HTML is almost certainly
 *    stale — force a one-time hard reload to pull the new index.html + chunks.
 *    A per-chunk sessionStorage flag prevents reload loops, so if the reloaded
 *    page STILL can't fetch the chunk we let the error bubble to the route's
 *    errorElement instead of reloading forever.
 *
 * @param importer the dynamic import, e.g. () => import('@/routes/admin/Foo')
 * @param chunkName a stable id for the reload flag (defaults to the import source)
 */
export function lazyWithRetry<T extends Record<string, unknown> = Record<string, never>>(
  importer: Importer<T>,
  chunkName = importer.toString(),
) {
  const flagKey = `${RELOAD_FLAG_PREFIX}${chunkName}`;

  return lazy(async () => {
    try {
      const mod = await retryImport(importer);
      // Success — clear any stale flag so a future deploy can reload again.
      window.sessionStorage.removeItem(flagKey);
      return mod;
    } catch (error) {
      if (isChunkLoadError(error) && !window.sessionStorage.getItem(flagKey)) {
        window.sessionStorage.setItem(flagKey, '1');
        window.location.reload();
        // Keep Suspense showing the loader while the page reloads.
        return await new Promise<never>(() => {});
      }
      throw error;
    }
  });
}

async function retryImport<T>(importer: Importer<T>, retries = 2, delayMs = 400) {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await importer();
    } catch (error) {
      lastError = error;
      if (!isChunkLoadError(error) || attempt === retries) break;
      await wait(delayMs * (attempt + 1));
    }
  }
  throw lastError;
}
